import { DIALOG_COLORS, Badge } from "../../../components/ui";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Swal from "sweetalert2";
import { brand } from "../../../config/theme";
import Modal from "../../../components/base/Modal";
import { buildThemeClasses } from "../theme";
import { leadDate, type Lead } from "../services/leadsService";
import { announceCallProblem } from "../services/callFeedback";
import { useSoftphone } from "../hooks/useSoftphone";
import SoftphonePanel from "../components/SoftphonePanel";
import PlaceTestCallModal from "../components/PlaceTestCallModal";
import {
  fetchCalls,
  deleteCall,
  refreshRecordingUrl,
  fetchCallTodo,
  fetchCompanyCallTodo,
  placeAiCall,
  placeCompanyAiCall,
  bulkPlaceAiCalls,
  bulkPlaceCompanyAiCalls,
  dispatchCallQueue,
  dismissCallTodo,
  restoreCallTodo,
  CALL_STATUS_META,
  fmtDuration,
  type Call,
  type CallerType,
  type CallTodoItem,
  type CompanyCallTodoItem,
  type DispatchResult,
} from "../services/callsService";
import { fetchAgents, type Agent } from "../services/agentsService";
import { useTheme } from "../../../context/ThemeContext";

// ===========================================================================
// Llamadas — LIVE section. The destination for AI (and manually logged human)
// calls: transcript, recording player, caller type (IA / Humano) and the
// post-call AI qualification. AI calls are placed automatically when a lead is
// created (Automatizaciones → Workflows) or manually from the Leads inbox.
// ===========================================================================

const NON_TERMINAL = new Set(["queued", "initiated", "in_progress"]);

function fmtDate(value: Call["createdAt"]): string {
  const d = leadDate(value ?? null);
  if (!d) return "—";
  return d.toLocaleString("es-MX", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const CALLER_META: Record<CallerType, { label: string; icon: string; tone: string }> = {
  ai: { label: "IA", icon: "ri-robot-2-line", tone: "bg-teal-500/15 text-teal-500" },
  human: { label: "Humano", icon: "ri-user-voice-line", tone: "bg-indigo-500/15 text-indigo-500" },
};

// Turn a raw `statusDetail` into a short, human reason for the status tooltip.
// Skipped calls are the common confusing case: in an environment without a voice
// provider key (ElevenLabs), every auto-triggered call is created and instantly
// skipped, so the reason is surfaced instead of a bare "Omitida".
function statusReason(call: Call): string | null {
  const d = call.statusDetail || "";
  if (/eleven|voice provider|not configured|api[_ ]?key/i.test(d)) {
    return "Proveedor de voz (ElevenLabs) no configurado en este entorno — la llamada no se marcó.";
  }
  if (d) return d;
  if (call.status === "skipped") return "Llamada omitida (proveedor de voz no disponible o lead sin teléfono).";
  return null;
}

// Message for a manual "Procesar cola" run. The dispatcher refuses the WHOLE
// queue outside the dial window (CALL_WINDOW_START–END), answering
// { dispatched: 0, capacity: 0, outsideWindow: true } — say so, instead of the
// misleading "no free capacity / nothing queued".
export function dispatchResultMessage(
  r: DispatchResult,
  maxConcurrent: number,
): { icon: "success" | "info"; title: string; html: string } {
  if (r.outsideWindow) {
    return {
      icon: "info",
      title: "Fuera del horario de marcado",
      html: "Las llamadas siguen en cola y salen solas cuando abra la ventana (por defecto 9:00–20:00, hora de Ciudad de México).",
    };
  }
  return r.dispatched
    ? {
        icon: "success",
        title: `${r.dispatched} llamada(s) iniciada(s)`,
        html: `Las demás esperan a que se libere la línea (${r.inFlight} en curso).`,
      }
    : {
        icon: "info",
        title: "Nada que procesar",
        html: `Sin capacidad libre o sin llamadas en cola (${r.inFlight} en curso, máximo ${maxConcurrent}).`,
      };
}

// Which sub-page this instance renders. The two subsections live in the lateral
// menu (Tareas → Llamadas → Por llamar / Historial), each its own route, so the
// view is fixed by the route — not switched in-page. Leads/Empresas remain tabs.
export type LlamadasView = "porllamar" | "historial";

export default function LlamadasLive({ view = "porllamar" }: { view?: LlamadasView }) {
  // CC-CONSOLE modals still take isDarkMode; WAYAK styles with tokens elsewhere.
  const { isDarkMode } = useTheme();
  const tc = buildThemeClasses();

  const [calls, setCalls] = useState<Call[]>([]);
  const [todo, setTodo] = useState<CallTodoItem[]>([]);
  const [companyTodo, setCompanyTodo] = useState<CompanyCallTodoItem[]>([]);
  const [aiAgents, setAiAgents] = useState<Agent[]>([]);
  const [companyAgentId, setCompanyAgentId] = useState<string>("");
  const [callingCompanyId, setCallingCompanyId] = useState<string | null>(null);
  const [companyBulking, setCompanyBulking] = useState(false);
  const [maxConcurrent, setMaxConcurrent] = useState(3);
  const [callingId, setCallingId] = useState<string | null>(null);
  const [bulking, setBulking] = useState(false);
  const [dispatching, setDispatching] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState<CallerType | "all">("all");
  // Hide "Omitida" (skipped) calls in the history — they pile up when there's no
  // voice provider key, so an opt-in filter keeps the list readable.
  const [hideSkipped, setHideSkipped] = useState(false);
  const [selected, setSelected] = useState<Call | null>(null);
  // Within "Por llamar": dial leads (people) or empresas (prospect companies).
  const [todoTab, setTodoTab] = useState<"leads" | "empresas">("leads");
  // Manual "test call" modal (pick an existing lead + agent + call type).
  const [showTestCall, setShowTestCall] = useState(false);
  const pollRef = useRef<number | null>(null);
  // Guards against overlapping polls: a slow request must not stack on the next
  // interval tick (that pile-up is what pushed requests past the 30s timeout).
  const inFlight = useRef(false);
  // Browser softphone for HUMAN test calls (shared floating panel).
  const softphone = useSoftphone();

  // Fetch only what the active sub-page needs — the history loads the call list;
  // "Por llamar" loads the backlogs + agents. Fetching everything on both views
  // kept `hasPending` permanently true (queued backlog) and polled forever.
  const load = useCallback(
    async (silent = false) => {
      if (inFlight.current) return;
      inFlight.current = true;
      if (!silent) setLoading(true);
      setError(null);
      try {
        if (view === "historial") {
          const page = await fetchCalls({ limit: 200 });
          setCalls(page.data);
          setSelected((prev) => (prev ? page.data.find((c) => c.id === prev.id) ?? prev : prev));
        } else {
          // To-do backlog is best-effort: never blocks the page.
          try {
            const t = await fetchCallTodo();
            setTodo(t.items);
            setMaxConcurrent(t.maxConcurrent);
          } catch {
            /* keep last known to-do */
          }
          // Empresas to call = prospect companies with a phone and no contact yet.
          try {
            const t = await fetchCompanyCallTodo();
            setCompanyTodo(t.items);
          } catch {
            /* keep last known companies */
          }
          // Active AI agents (product-intro first). Only refreshed on explicit
          // (non-silent) loads — agents rarely change, so silent polls skip them.
          if (!silent || aiAgents.length === 0) {
            try {
              const page = await fetchAgents({ limit: 300 });
              const ai = page.data
                .filter((a) => a.type === "ai" && a.status === "active")
                .sort((a, b) => Number(b.useCase === "product_intro") - Number(a.useCase === "product_intro"));
              setAiAgents(ai);
              setCompanyAgentId((prev) => prev || ai[0]?.id || "");
            } catch {
              /* keep last known agents */
            }
          }
        }
      } catch (e) {
        const err = e as { response?: { data?: { message?: string } }; message?: string };
        setError(err.response?.data?.message || err.message || "No se pudieron cargar las llamadas");
      } finally {
        if (!silent) setLoading(false);
        inFlight.current = false;
      }
    },
    [view, aiAgents.length],
  );

  // Announce a placed call that came back terminal (failed/skipped) with its
  // reason, instead of a misleading "iniciada". Returns true if it warned, so
  // callers only show their success message for a genuinely live call.
  // (Shared with the other "place a call" surfaces — see services/callFeedback.)
  const announceIfProblem = announceCallProblem;

  // Place a cold AI call to a prospect company with the selected AI agent. The
  // backend captures a contact and turns it into a lead (not auto-dialed).
  const callCompany = async (company: CompanyCallTodoItem) => {
    if (!companyAgentId) return;
    setCallingCompanyId(company.companyId);
    try {
      const call = await placeCompanyAiCall(company.companyId, companyAgentId);
      await load(true);
      if (!announceIfProblem(call)) {
        Swal.fire({
          icon: "success",
          title: "Llamada iniciada",
          text: `El agente está llamando a ${company.name ?? "la empresa"}. Si consigue un contacto, se creará un lead automáticamente (sin volver a llamarlo).`,
          confirmButtonColor: brand.primary,
        });
      }
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      Swal.fire({
        icon: "error",
        title: "No se pudo iniciar la llamada",
        text: err.response?.data?.message || err.message || "Intenta de nuevo",
        confirmButtonColor: brand.primary,
      });
    } finally {
      setCallingCompanyId(null);
    }
  };

  // Enqueue cold AI calls for every pending company (throttled by the backend,
  // same shared line as leads). Uses the selected product-intro agent.
  const callAllCompanies = async () => {
    const callable = companyTodo.filter((c) => !c.queueStatus).length;
    if (callable === 0 || !companyAgentId) return;
    const res = await Swal.fire({
      icon: "question",
      title: `Llamar a ${callable} empresa(s)`,
      html: `El agente IA llamará a cada empresa para conseguir un contacto. Las llamadas se ponen en cola y se realizan <b>${maxConcurrent} a la vez</b> (una sola línea, compartida con los leads).`,
      showCancelButton: true,
      confirmButtonText: "Llamar a todas",
      cancelButtonText: "Cancelar",
      confirmButtonColor: brand.primary,
    });
    if (!res.isConfirmed) return;
    setCompanyBulking(true);
    try {
      const r = await bulkPlaceCompanyAiCalls({ all: true, crmAgentId: companyAgentId });
      await load(true);
      Swal.fire({
        icon: "success",
        title: "Llamadas en cola",
        html: `<b>${r.queued}</b> en cola · ${r.skipped} omitidas. Se irán marcando ${maxConcurrent} a la vez.`,
        confirmButtonColor: brand.primary,
      });
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.response?.data?.message || err.message || "No se pudo encolar",
        confirmButtonColor: brand.primary,
      });
    } finally {
      setCompanyBulking(false);
    }
  };

  useEffect(() => {
    load();
  }, [load]);

  // Auto-refresh only while there's something LIVE on THIS view: an in-progress
  // call in the history, or a queued call waiting to dial in "Por llamar".
  // Terminal calls (completed/skipped/failed) never trigger polling.
  const hasPending = useMemo(
    () =>
      view === "historial"
        ? calls.some((c) => NON_TERMINAL.has(c.status))
        : todo.some((t) => t.queueStatus) || companyTodo.some((t) => t.queueStatus),
    [view, calls, todo, companyTodo],
  );
  useEffect(() => {
    if (!hasPending) return;
    pollRef.current = window.setInterval(() => load(true), 12000);
    return () => {
      if (pollRef.current) window.clearInterval(pollRef.current);
    };
  }, [hasPending, load]);

  // After a human softphone call ends, refresh so its record shows up.
  useEffect(() => {
    if (softphone.state.status !== "ended") return;
    const id = window.setTimeout(() => load(true), 12000);
    return () => window.clearTimeout(id);
  }, [softphone.state.status, load]);

  // Place a single AI call now (immediate, like the per-lead call in the inbox).
  const callOne = async (item: CallTodoItem) => {
    setCallingId(item.leadId);
    try {
      const call = await placeAiCall(item.leadId);
      await load(true);
      announceIfProblem(call);
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      Swal.fire({
        icon: "error",
        title: "No se pudo iniciar la llamada",
        text: err.response?.data?.message || err.message || "Intenta de nuevo",
        confirmButtonColor: brand.primary,
      });
    } finally {
      setCallingId(null);
    }
  };

  // Enqueue AI calls for every pending new lead (throttled by the backend).
  const callAll = async () => {
    if (todo.length === 0) return;
    const res = await Swal.fire({
      icon: "question",
      title: `Llamar a ${todo.length} lead(s) nuevo(s)`,
      html: `Las llamadas se ponen en cola y se realizan <b>${maxConcurrent} a la vez</b> para no saturar la línea. Cada lead pasa a <b>Contactado</b> al terminar.`,
      showCancelButton: true,
      confirmButtonText: "Llamar a todos",
      cancelButtonText: "Cancelar",
      confirmButtonColor: brand.primary,
    });
    if (!res.isConfirmed) return;
    setBulking(true);
    try {
      const r = await bulkPlaceAiCalls({ all: true });
      await load(true);
      Swal.fire({
        icon: "success",
        title: "Llamadas en cola",
        html: `<b>${r.queued}</b> en cola · ${r.skipped} omitidas. Se irán marcando ${maxConcurrent} a la vez.`,
        confirmButtonColor: brand.primary,
      });
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.response?.data?.message || err.message || "No se pudo encolar",
        confirmButtonColor: brand.primary,
      });
    } finally {
      setBulking(false);
    }
  };

  // Run the dispatcher now instead of waiting for the scheduled (hourly) run.
  const processQueue = async () => {
    setDispatching(true);
    try {
      const r = await dispatchCallQueue();
      await load(true);
      Swal.fire({ ...dispatchResultMessage(r, maxConcurrent), confirmButtonColor: brand.primary });
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.response?.data?.message || err.message || "No se pudo procesar la cola",
        confirmButtonColor: brand.primary,
      });
    } finally {
      setDispatching(false);
    }
  };

  // Manually remove lead(s) from the backlog (single row or a selected batch).
  // Optimistic: drop them now, cancel any queued call server-side, and offer a
  // brief Undo. Non-destructive — the lead stays in the CRM, just off this list.
  const dismissTodo = async (leadIds: string[]) => {
    if (leadIds.length === 0) return;
    setTodo((prev) => prev.filter((t) => !leadIds.includes(t.leadId)));
    try {
      await dismissCallTodo(leadIds);
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      await load(true);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.response?.data?.message || err.message || "No se pudo quitar de la lista",
        confirmButtonColor: brand.primary,
      });
      return;
    }
    const toast = await Swal.fire({
      toast: true,
      position: "bottom-end",
      icon: "success",
      title: leadIds.length === 1 ? "Lead quitado de la lista" : `${leadIds.length} leads quitados de la lista`,
      showConfirmButton: true,
      confirmButtonText: "Deshacer",
      confirmButtonColor: brand.primary,
      timer: 6000,
      timerProgressBar: true,
    });
    if (toast.isConfirmed) {
      try {
        await restoreCallTodo(leadIds);
      } finally {
        await load(true);
      }
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return calls.filter((c) => {
      if (typeFilter !== "all" && c.callerType !== typeFilter) return false;
      if (hideSkipped && c.status === "skipped") return false;
      if (!q) return true;
      return [c.to, c.summary, c.transcript, c.leadId]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
  }, [calls, query, typeFilter, hideSkipped]);

  const skippedCount = useMemo(() => calls.filter((c) => c.status === "skipped").length, [calls]);

  const counts = useMemo(
    () => ({
      all: calls.length,
      ai: calls.filter((c) => c.callerType === "ai").length,
      human: calls.filter((c) => c.callerType === "human").length,
    }),
    [calls],
  );

  const removeCall = async (call: Call) => {
    const res = await Swal.fire({
      icon: "warning",
      title: "Eliminar llamada",
      text: "¿Eliminar este registro de llamada? Esta acción no se puede deshacer.",
      showCancelButton: true,
      confirmButtonText: "Eliminar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: DIALOG_COLORS.danger,
    });
    if (!res.isConfirmed) return;
    setCalls((prev) => prev.filter((c) => c.id !== call.id));
    if (selected?.id === call.id) setSelected(null);
    try {
      await deleteCall(call.id);
    } catch {
      await load();
      Swal.fire({ icon: "error", title: "Error", text: "No se pudo eliminar", confirmButtonColor: brand.primary });
    }
  };

  return (
    <div className="h-full w-full overflow-y-auto p-6">
      <div className="mx-auto max-w-6xl">
        {/* Header */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-500/15 text-primary-500">
              <i className="ri-phone-line text-xl" />
            </span>
            <div>
              <h1 className={`text-xl font-bold ${tc.text}`}>
                {view === "porllamar" ? "Llamadas — Por llamar" : "Llamadas — Historial"}
              </h1>
              <p className={`text-sm ${tc.textSecondary}`}>
                {view === "porllamar"
                  ? "Leads y empresas pendientes de llamar"
                  : "Grabación, transcripción y calificación de cada llamada (IA o humana)"}
              </p>
            </div>
          </div>

          <button
            onClick={() => load()}
            className="flex items-center gap-2 rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-on-primary transition-colors hover:bg-primary-400"
          >
            <i className={`ri-refresh-line ${loading ? "animate-spin" : ""}`} />
            <span className="whitespace-nowrap">Actualizar</span>
          </button>
        </div>

        {view === "porllamar" ? (
          <>
            {/* Tabs inside the to-do backlog: dial leads (people) or empresas. */}
            <div className="mb-4 flex flex-wrap gap-2">
              <Chip active={todoTab === "leads"} label="Leads" count={todo.length} onClick={() => setTodoTab("leads")} />
              <Chip active={todoTab === "empresas"} label="Empresas" count={companyTodo.length} onClick={() => setTodoTab("empresas")} />
            </div>

            {todoTab === "leads" ? (
              <CallTodoPanel
                todo={todo}
                maxConcurrent={maxConcurrent}
                callingId={callingId}
                bulking={bulking}
                dispatching={dispatching}
                onCallOne={callOne}
                onCallAll={callAll}
                onProcessQueue={processQueue}
                onDismiss={dismissTodo}
                onOpenTestCall={() => setShowTestCall(true)}
                tc={tc}
              />
            ) : (
              <EmpresasTodoPanel
                companies={companyTodo}
                agents={aiAgents}
                agentId={companyAgentId}
                onSelectAgent={setCompanyAgentId}
                callingCompanyId={callingCompanyId}
                onCallCompany={callCompany}
                onCallAll={callAllCompanies}
                onProcessQueue={processQueue}
                bulking={companyBulking}
                dispatching={dispatching}
                maxConcurrent={maxConcurrent}
                tc={tc}
              />
            )}
          </>
        ) : (
        <>
        {/* History header + search */}
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <h2 className={`text-sm font-semibold uppercase tracking-wide ${tc.textSecondary}`}>
            Historial de llamadas
          </h2>
          <div className={`flex items-center gap-2 rounded-lg border px-3 py-2 ${tc.border} ${tc.cardBg}`}>
            <i className={`ri-search-line ${tc.textSecondary}`} />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar llamadas..."
              className={`bg-transparent text-sm outline-none ${tc.text}`}
            />
          </div>
        </div>

        {/* Type filter chips */}
        <div className="mb-6 flex flex-wrap items-center gap-2">
          <Chip active={typeFilter === "all"} label="Todas" count={counts.all} onClick={() => setTypeFilter("all")} />
          <Chip active={typeFilter === "ai"} label="IA" count={counts.ai} tone={CALLER_META.ai.tone} onClick={() => setTypeFilter("ai")} />
          <Chip active={typeFilter === "human"} label="Humano" count={counts.human} tone={CALLER_META.human.tone} onClick={() => setTypeFilter("human")} />
          {skippedCount > 0 && (
            <label
              title="Oculta las llamadas omitidas (creadas cuando no hay proveedor de voz configurado)"
              className={`ml-auto flex cursor-pointer items-center gap-2 rounded-xl border px-3 py-2 text-sm font-medium ${tc.border} ${tc.cardBg} ${tc.text}`}
            >
              <input
                type="checkbox"
                checked={hideSkipped}
                onChange={(e) => setHideSkipped(e.target.checked)}
                className="h-4 w-4 cursor-pointer accent-primary-500"
              />
              Ocultar omitidas ({skippedCount})
            </label>
          )}
        </div>

        {/* Table */}
        <div className={`rounded-2xl border ${tc.cardBorder} ${tc.cardBg}`}>
          {loading ? (
            <div className={`p-10 text-center text-sm ${tc.textSecondary}`}>
              <i className="ri-loader-4-line mr-2 animate-spin" />
              Cargando llamadas...
            </div>
          ) : error ? (
            <div className="p-10 text-center">
              <i className="ri-error-warning-line mb-2 text-3xl text-red-500" />
              <p className="text-sm text-red-500">{error}</p>
              <button onClick={() => load()} className="mt-3 rounded-lg bg-primary-500 px-4 py-2 text-sm text-on-primary hover:bg-primary-400">
                Reintentar
              </button>
            </div>
          ) : filtered.length === 0 ? (
            <div className={`p-10 text-center text-sm ${tc.textSecondary}`}>
              <i className="ri-phone-line mb-2 block text-3xl" />
              No hay llamadas todavía. Las llamadas de IA se crean automáticamente cuando llega un lead (Automatizaciones → Workflows).
            </div>
          ) : (
            <div className="w-full overflow-x-auto p-4">
              <table className="w-full overflow-hidden rounded-xl text-xs shadow-sm">
                <thead className="bg-primary-500 text-on-primary">
                  <tr>
                    <th className="p-3 text-left font-bold">Fecha</th>
                    <th className="p-3 text-left font-bold">Contacto</th>
                    <th className="p-3 text-center font-bold">Tipo</th>
                    <th className="p-3 text-left font-bold">Resumen</th>
                    <th className="p-3 text-center font-bold">Estado</th>
                    <th className="p-3 text-center font-bold">Duración</th>
                    <th className="p-3 text-center font-bold">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((c, i) => (
                    <tr
                      key={c.id}
                      className={`text-content ${i % 2 === 0 ? "bg-surface" : "bg-card"}`}
                    >
                      <td className="p-2 whitespace-nowrap">{fmtDate(c.createdAt)}</td>
                      <td className="p-2 font-medium">{c.to ?? "—"}</td>
                      <td className="p-2 text-center">
                        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${CALLER_META[c.callerType].tone}`}>
                          <i className={CALLER_META[c.callerType].icon} />
                          {CALLER_META[c.callerType].label}
                        </span>
                      </td>
                      <td className="p-2 max-w-[18rem] truncate">{c.summary ?? (c.transcript ? "Transcripción disponible" : "—")}</td>
                      <td className="p-2 text-center">
                        <span title={statusReason(c) ?? undefined} className="inline-flex items-center gap-1">
                        <Badge tone={CALL_STATUS_META[c.status]?.tone ?? "neutral"} size="sm">
                          {CALL_STATUS_META[c.status]?.label ?? c.status}
                        </Badge>
                          {statusReason(c) && <i className="ri-information-line cursor-help opacity-70" />}
                        </span>
                      </td>
                      <td className="p-2 text-center whitespace-nowrap">{fmtDuration(c.durationSecs)}</td>
                      <td className="p-2">
                        <div className="flex items-center justify-center gap-1">
                          {c.recordingUrl && <i title="Grabación disponible" className="ri-mic-line text-emerald-500" />}
                          <button title="Ver detalle" onClick={() => setSelected(c)} className="rounded-md p-1.5 text-primary-500 hover:bg-primary-500/10">
                            <i className="ri-eye-line" />
                          </button>
                          <button title="Eliminar" onClick={() => removeCall(c)} className="rounded-md p-1.5 text-red-500 hover:bg-red-500/10">
                            <i className="ri-delete-bin-line" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <p className={`mt-3 text-xs ${tc.textSecondary}`}>
          <i className="ri-information-line mr-1" />
          Datos en vivo. La grabación y transcripción aparecen al terminar la llamada (webhook de ElevenLabs). El costo del modelo se rastrea en Langfuse.
        </p>
        </>
        )}
      </div>

      {selected && (
        <CallDetailModal call={selected} tc={tc} onClose={() => setSelected(null)} />
      )}

      {showTestCall && (
        <PlaceTestCallModal
          tc={tc}
          isDarkMode={isDarkMode}
          onClose={() => setShowTestCall(false)}
          onPlaceAi={async (leadId, agentId) => {
            const call = await placeAiCall(leadId, agentId);
            await load(true);
            if (!announceIfProblem(call)) {
              Swal.fire({
                icon: "success",
                title: "Llamada iniciada",
                text: "El agente de IA está llamando al lead. La grabación, transcripción y calificación aparecerán en el Historial al terminar.",
                confirmButtonColor: brand.primary,
              });
            }
          }}
          onStartHuman={(lead: Lead, agentId?: string) => softphone.startCall(lead, agentId)}
        />
      )}

      {/* Floating browser softphone (human test calls) */}
      <SoftphonePanel
        state={softphone.state}
        onHangup={softphone.hangup}
        onToggleMute={softphone.toggleMute}
        onClose={softphone.reset}
      />
    </div>
  );
}

// --- "Por llamar hoy" panel -------------------------------------------------
// The agent's call backlog = leads still in "Nuevo" with a phone and no AI call
// yet (mirrors the pipeline's Nuevo column). Each is dialed individually now, or
// all at once via the throttled queue. Completing a call moves the lead to
// Contactado, so it drops off this list.

const QUEUE_BADGE: Record<"pending" | "dispatched", { label: string; tone: string; icon: string }> = {
  pending: { label: "En cola", tone: "bg-gray-400/15 text-gray-500", icon: "ri-time-line" },
  dispatched: { label: "Marcando…", tone: "bg-amber-500/15 text-amber-600 dark:text-amber-400", icon: "ri-phone-line" },
};

function CallTodoPanel({
  todo,
  maxConcurrent,
  callingId,
  bulking,
  dispatching,
  onCallOne,
  onCallAll,
  onProcessQueue,
  onDismiss,
  onOpenTestCall,
  tc,
}: {
  todo: CallTodoItem[];
  maxConcurrent: number;
  callingId: string | null;
  bulking: boolean;
  dispatching: boolean;
  onCallOne: (item: CallTodoItem) => void;
  onCallAll: () => void;
  onProcessQueue: () => void;
  onDismiss: (leadIds: string[]) => void;
  onOpenTestCall: () => void;
  tc: ReturnType<typeof buildThemeClasses>;
}) {
  const callable = todo.filter((t) => !t.queueStatus).length;
  // How many calls are sitting in the queue waiting to dial (worth a manual run).
  const queued = todo.filter((t) => t.queueStatus === "pending").length;

  // Multi-select for batch removal. Pruned to ids still present in the backlog.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  useEffect(() => {
    setSelected((prev) => {
      const ids = new Set(todo.map((t) => t.leadId));
      const next = new Set([...prev].filter((id) => ids.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [todo]);

  const allSelected = todo.length > 0 && selected.size === todo.length;
  const toggleOne = (leadId: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(leadId)) next.delete(leadId);
      else next.add(leadId);
      return next;
    });
  const toggleAll = () =>
    setSelected(allSelected ? new Set() : new Set(todo.map((t) => t.leadId)));
  const removeSelected = () => {
    if (selected.size === 0) return;
    onDismiss([...selected]);
    setSelected(new Set());
  };

  return (
    <div className={`mb-6 rounded-2xl border ${tc.cardBorder} ${tc.cardBg}`}>
      <div className={`flex flex-wrap items-center justify-between gap-3 border-b p-4 ${tc.cardBorder}`}>
        <div className="flex items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-sky-500/15 text-sky-500">
            <i className="ri-phone-find-line text-lg" />
          </span>
          <div>
            <h2 className={`text-sm font-bold ${tc.text}`}>Por llamar hoy</h2>
            <p className={`text-xs ${tc.textSecondary}`}>
              Leads nuevos que el agente de IA debe llamar
            </p>
          </div>
          <span className="ml-1 rounded-full bg-sky-500/15 px-2 py-0.5 text-xs font-semibold text-sky-500">
            {todo.length}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onOpenTestCall}
            title="Probar una llamada: elige un lead existente, el tipo de llamada y el agente"
            className="flex items-center gap-2 rounded-lg border border-primary-500 px-3 py-2 text-sm font-medium text-primary-500 transition-colors hover:bg-primary-500/10"
          >
            <i className="ri-add-line" />
            <span className="whitespace-nowrap">Probar llamada</span>
          </button>
          {selected.size > 0 && (
            <button
              onClick={removeSelected}
              title="Quitar los seleccionados de la lista (cancela su llamada en cola)"
              className="flex items-center gap-2 rounded-lg border border-red-500 px-4 py-2 text-sm font-medium text-red-500 transition-colors hover:bg-red-500/10"
            >
              <i className="ri-delete-bin-line" />
              <span className="whitespace-nowrap">Eliminar ({selected.size})</span>
            </button>
          )}
          {queued > 0 && (
            <button
              onClick={onProcessQueue}
              disabled={dispatching}
              title="Iniciar ahora las llamadas en cola sin esperar al proceso automático"
              className={`flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium transition-colors hover:border-primary-500 disabled:cursor-not-allowed disabled:opacity-50 ${tc.border} ${tc.cardBg} ${tc.text}`}
            >
              <i className={dispatching ? "ri-loader-4-line animate-spin" : "ri-play-circle-line"} />
              <span className="whitespace-nowrap">Procesar cola ({queued})</span>
            </button>
          )}
          <button
            onClick={onCallAll}
            disabled={bulking || callable === 0}
            className="flex items-center gap-2 rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-on-primary transition-colors hover:bg-primary-400 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <i className={bulking ? "ri-loader-4-line animate-spin" : "ri-phone-fill"} />
            <span className="whitespace-nowrap">Llamar a todos</span>
          </button>
        </div>
      </div>

      {todo.length === 0 ? (
        <div className={`p-6 text-center text-sm ${tc.textSecondary}`}>
          <i className="ri-checkbox-circle-line mr-1 text-emerald-500" />
          Sin llamadas pendientes. Los leads nuevos con teléfono aparecerán aquí.
        </div>
      ) : (
        <>
          {/* Select-all bar for batch removal. */}
          <div className={`flex items-center gap-3 border-b px-4 py-2 ${tc.cardBorder}`}>
            <input
              type="checkbox"
              checked={allSelected}
              ref={(el) => {
                if (el) el.indeterminate = selected.size > 0 && !allSelected;
              }}
              onChange={toggleAll}
              className="h-4 w-4 cursor-pointer accent-primary-500"
            />
            <span className={`text-xs ${tc.textSecondary}`}>
              {selected.size > 0 ? `${selected.size} seleccionado(s)` : "Seleccionar todo"}
            </span>
          </div>

          <div className="max-h-72 divide-y overflow-y-auto divide-line">
            {todo.map((item) => {
              const badge = item.queueStatus ? QUEUE_BADGE[item.queueStatus] : null;
              return (
                <div key={item.leadId} className="flex items-center justify-between gap-3 px-4 py-2.5">
                  <div className="flex min-w-0 items-center gap-3">
                    <input
                      type="checkbox"
                      checked={selected.has(item.leadId)}
                      onChange={() => toggleOne(item.leadId)}
                      className="h-4 w-4 flex-shrink-0 cursor-pointer accent-primary-500"
                    />
                    <div className="min-w-0">
                      <p className={`truncate text-sm font-medium ${tc.text}`}>
                        {item.name ?? "Lead sin nombre"}
                        {item.attempted && (
                          <span className="ml-2 rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-medium text-amber-600 dark:text-amber-400">
                            Reintento
                          </span>
                        )}
                        {item.autoCallSkipped === "budget_exhausted" && (
                          <span
                            title="La llamada IA automática no se hizo porque se agotó el cupo global por hora de leads públicos. Llámalo manualmente."
                            className="ml-2 rounded bg-red-500/15 px-1.5 py-0.5 text-[10px] font-medium text-red-600 dark:text-red-400"
                          >
                            Sin cupo IA
                          </span>
                        )}
                      </p>
                      <p className={`truncate text-xs ${tc.textSecondary}`}>
                        {[item.company, item.to, item.product].filter(Boolean).join(" · ") || "—"}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-shrink-0 items-center gap-2">
                    {badge ? (
                      <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${badge.tone}`}>
                        <i className={`${badge.icon} ${item.queueStatus === "dispatched" ? "animate-pulse" : ""}`} />
                        {badge.label}
                      </span>
                    ) : (
                      <button
                        onClick={() => onCallOne(item)}
                        disabled={callingId === item.leadId}
                        className="flex items-center gap-1.5 rounded-lg border border-primary-500 px-3 py-1.5 text-xs font-medium text-primary-500 transition-colors hover:bg-primary-500/10 disabled:opacity-50"
                      >
                        <i className={callingId === item.leadId ? "ri-loader-4-line animate-spin" : "ri-phone-line"} />
                        Llamar
                      </button>
                    )}
                    <button
                      title="Quitar de la lista"
                      onClick={() => onDismiss([item.leadId])}
                      className="rounded-md p-1.5 text-red-500 transition-colors hover:bg-red-500/10"
                    >
                      <i className="ri-delete-bin-line" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      <p className={`border-t p-3 text-xs ${tc.cardBorder} ${tc.textSecondary}`}>
        <i className="ri-information-line mr-1" />
        Las llamadas masivas se realizan en cola, {maxConcurrent} a la vez (una sola línea). La cola se
        procesa automáticamente cada hora; usa <b>Procesar cola</b> para iniciarlas ya. Al completarse,
        el lead pasa a <b>Contactado</b> y sale de esta lista.
      </p>
    </div>
  );
}

// --- "Por llamar — Empresas" panel ------------------------------------------
// Businesses found by web prospecting are staged as `crm_companies`
// (stage="prospect") carrying the source phone/whatsapp, before anyone adds a
// named contact person. Those are the cold-call targets here. The AI agent
// (chosen above) introduces a product and tries to capture a contact; when it
// does, the backend creates a lead from the call (NOT auto-dialed).

function EmpresasTodoPanel({
  companies,
  agents,
  agentId,
  onSelectAgent,
  callingCompanyId,
  onCallCompany,
  onCallAll,
  onProcessQueue,
  bulking,
  dispatching,
  maxConcurrent,
  tc,
}: {
  companies: CompanyCallTodoItem[];
  agents: Agent[];
  agentId: string;
  onSelectAgent: (id: string) => void;
  callingCompanyId: string | null;
  onCallCompany: (company: CompanyCallTodoItem) => void;
  onCallAll: () => void;
  onProcessQueue: () => void;
  bulking: boolean;
  dispatching: boolean;
  maxConcurrent: number;
  tc: ReturnType<typeof buildThemeClasses>;
}) {
  const selectCls = `rounded-lg border px-3 py-2 text-sm outline-none border-line bg-app text-content`;
  const noAgents = agents.length === 0;
  const canCall = Boolean(agentId) && !noAgents;
  const callable = companies.filter((c) => !c.queueStatus).length;
  // How many company calls are queued waiting to dial (worth a manual run).
  const queued = companies.filter((c) => c.queueStatus === "pending").length;

  return (
    <div className={`mb-6 rounded-2xl border ${tc.cardBorder} ${tc.cardBg}`}>
      <div className={`flex flex-wrap items-center justify-between gap-3 border-b p-4 ${tc.cardBorder}`}>
        <div className="flex items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-violet-500/15 text-violet-500">
            <i className="ri-building-2-line text-lg" />
          </span>
          <div>
            <h2 className={`text-sm font-bold ${tc.text}`}>Empresas por llamar</h2>
            <p className={`text-xs ${tc.textSecondary}`}>
              Negocios de prospección con teléfono y sin contacto asignado
            </p>
          </div>
          <span className="ml-1 rounded-full bg-violet-500/15 px-2 py-0.5 text-xs font-semibold text-violet-500">
            {companies.length}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* AI agent picker — which product the calls promote. */}
          <label className={`text-xs ${tc.textSecondary}`}>Agente IA</label>
          <select
            value={agentId}
            onChange={(e) => onSelectAgent(e.target.value)}
            disabled={noAgents}
            className={`${selectCls} disabled:cursor-not-allowed disabled:opacity-50`}
          >
            {noAgents ? (
              <option value="">Sin agentes IA activos</option>
            ) : (
              agents.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                  {a.product ? ` · ${a.product}` : ""}
                </option>
              ))
            )}
          </select>
          {queued > 0 && (
            <button
              onClick={onProcessQueue}
              disabled={dispatching}
              title="Iniciar ahora las llamadas en cola sin esperar al proceso automático"
              className={`flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium transition-colors hover:border-primary-500 disabled:cursor-not-allowed disabled:opacity-50 ${tc.border} ${tc.cardBg} ${tc.text}`}
            >
              <i className={dispatching ? "ri-loader-4-line animate-spin" : "ri-play-circle-line"} />
              <span className="whitespace-nowrap">Procesar cola ({queued})</span>
            </button>
          )}
          <button
            onClick={onCallAll}
            disabled={bulking || !canCall || callable === 0}
            title={canCall ? "Llamar con IA a todas las empresas pendientes" : "Crea o activa un agente IA para llamar"}
            className="flex items-center gap-2 rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-on-primary transition-colors hover:bg-primary-400 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <i className={bulking ? "ri-loader-4-line animate-spin" : "ri-phone-fill"} />
            <span className="whitespace-nowrap">Llamar a todas</span>
          </button>
        </div>
      </div>

      {companies.length === 0 ? (
        <div className={`p-6 text-center text-sm ${tc.textSecondary}`}>
          <i className="ri-checkbox-circle-line mr-1 text-emerald-500" />
          Sin empresas pendientes. Los negocios de Automatizaciones → Prospección con teléfono aparecerán aquí.
        </div>
      ) : (
        <div className="max-h-72 divide-y overflow-y-auto divide-line">
          {companies.map((c) => {
            const badge = c.queueStatus ? QUEUE_BADGE[c.queueStatus] : null;
            return (
              <div key={c.companyId} className="flex items-center justify-between gap-3 px-4 py-2.5">
                <div className="min-w-0">
                  <p className={`truncate text-sm font-medium ${tc.text}`}>
                    {c.name ?? "Empresa sin nombre"}
                    {c.attempted && (
                      <span className="ml-2 rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-medium text-amber-600 dark:text-amber-400">
                        Reintento
                      </span>
                    )}
                  </p>
                  <p className={`truncate text-xs ${tc.textSecondary}`}>
                    {[c.to, c.industry, c.address].filter(Boolean).join(" · ") || "—"}
                  </p>
                </div>
                {badge ? (
                  <span className={`inline-flex flex-shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${badge.tone}`}>
                    <i className={`${badge.icon} ${c.queueStatus === "dispatched" ? "animate-pulse" : ""}`} />
                    {badge.label}
                  </span>
                ) : (
                  <button
                    onClick={() => onCallCompany(c)}
                    disabled={!canCall || callingCompanyId === c.companyId}
                    title={canCall ? "Llamar con IA para conseguir un contacto" : "Crea o activa un agente IA para llamar"}
                    className="flex flex-shrink-0 items-center gap-1.5 rounded-lg border border-primary-500 px-3 py-1.5 text-xs font-medium text-primary-500 transition-colors hover:bg-primary-500/10 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <i className={callingCompanyId === c.companyId ? "ri-loader-4-line animate-spin" : "ri-phone-line"} />
                    Llamar
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      <p className={`border-t p-3 text-xs ${tc.cardBorder} ${tc.textSecondary}`}>
        <i className="ri-information-line mr-1" />
        El agente IA llama a cada empresa para presentar el producto y conseguir un contacto. Las llamadas
        masivas se realizan en cola, {maxConcurrent} a la vez (una sola línea, compartida con los leads); usa{" "}
        <b>Procesar cola</b> para iniciarlas ya. Si consigue un contacto, se crea un <b>lead</b> con esos datos
        (y <b>no</b> se le vuelve a llamar automáticamente).
      </p>
    </div>
  );
}

function CallDetailModal({
  call,
  tc,
  onClose,
}: {
  call: Call;
  tc: ReturnType<typeof buildThemeClasses>;
  onClose: () => void;
}) {
  const [recUrl, setRecUrl] = useState<string | null>(call.recordingUrl);
  const [refreshing, setRefreshing] = useState(false);

  const refreshRec = async () => {
    setRefreshing(true);
    try {
      const url = await refreshRecordingUrl(call.id);
      setRecUrl(url);
    } catch {
      /* ignore */
    } finally {
      setRefreshing(false);
    }
  };

  const q = call.qualification;

  return (
    <Modal
      title={`Llamada — ${call.to ?? "—"}`}
      onClose={onClose}
      themeClasses={tc}
      width="max-w-3xl"
      content={
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${CALLER_META[call.callerType].tone}`}>
              <i className={CALLER_META[call.callerType].icon} /> {CALLER_META[call.callerType].label}
            </span>
            <Badge tone={CALL_STATUS_META[call.status]?.tone ?? "neutral"}>
              {CALL_STATUS_META[call.status]?.label ?? call.status}
            </Badge>
            <span className={`text-xs ${tc.textSecondary}`}>{fmtDuration(call.durationSecs)}</span>
            <span className={`text-xs ${tc.textSecondary}`}>· {fmtDate(call.createdAt)}</span>
          </div>

          {call.statusDetail && call.status !== "completed" && (
            <p className="rounded-lg bg-amber-500/10 px-3 py-2 text-xs text-amber-600 dark:text-amber-400">
              {call.statusDetail}
            </p>
          )}

          {/* Recording */}
          <div>
            <p className={`mb-1 text-xs font-medium ${tc.textSecondary}`}>Grabación</p>
            {recUrl ? (
              <audio controls preload="none" src={recUrl} className="w-full">
                Tu navegador no soporta audio.
              </audio>
            ) : (
              <div className="flex items-center gap-3">
                <p className={`text-sm ${tc.textSecondary}`}>Sin grabación disponible.</p>
                {/* recordingPath → re-sign an expired URL; otherwise an AI call
                    with a conversationId can still pull the audio from the
                    provider (the post-call webhook often runs before it's ready). */}
                {(call.recordingPath || (call.callerType === "ai" && call.conversationId)) && (
                  <button onClick={refreshRec} className="text-xs text-primary-500 hover:underline">
                    <i className={`ri-refresh-line mr-1 ${refreshing ? "animate-spin" : ""}`} />
                    {call.recordingPath ? "Regenerar enlace" : "Recuperar grabación"}
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Qualification */}
          {q && (
            <div className={`rounded-xl border p-3 ${tc.border}`}>
              <div className="mb-2 flex items-center justify-between">
                <p className={`text-sm font-semibold ${tc.text}`}>
                  <i className="ri-sparkling-2-line mr-1 text-teal-500" /> Calificación IA
                </p>
                <span className="rounded-full bg-primary-500/15 px-2 py-0.5 text-xs font-medium text-primary-500">
                  Score {q.score}/100
                </span>
              </div>
              {q.summary && <p className={`mb-2 text-sm ${tc.text}`}>{q.summary}</p>}
              <div className="grid grid-cols-1 gap-2 text-xs sm:grid-cols-2">
                <Info label="Datos correctos" value={q.dataAccurate ? "Sí" : "No"} tc={tc} />
                <Info label="Estado recomendado" value={q.recommendedStatus} tc={tc} />
              </div>
              {q.reasoning && <p className={`mt-2 text-xs ${tc.textSecondary}`}>{q.reasoning}</p>}
              {q.corrections && Object.values(q.corrections).some((v) => v) && (
                <div className="mt-2">
                  <p className={`text-xs font-medium ${tc.textSecondary}`}>Correcciones detectadas</p>
                  <ul className={`mt-1 list-inside list-disc text-xs ${tc.text}`}>
                    {Object.entries(q.corrections)
                      .filter(([, v]) => v)
                      .map(([k, v]) => (
                        <li key={k}>
                          <span className={tc.textSecondary}>{k}:</span> {String(v)}
                        </li>
                      ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Transcript */}
          <div>
            <p className={`mb-2 text-xs font-medium ${tc.textSecondary}`}>Transcripción</p>
            {call.transcriptSegments && call.transcriptSegments.length > 0 ? (
              <div className={`max-h-72 space-y-2 overflow-y-auto rounded-xl border p-3 ${tc.border}`}>
                {call.transcriptSegments.map((s, i) => (
                  <div key={i} className={`flex ${s.role === "agent" ? "justify-start" : "justify-end"}`}>
                    <div
                      className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${
                        s.role === "agent"
                          ? "bg-teal-500/15 text-teal-900 dark:text-teal-100"
                          : "bg-primary-500/15 text-primary-900 dark:text-primary-100"
                      }`}
                    >
                      <span className={`mb-0.5 block text-[10px] font-semibold ${tc.textSecondary}`}>
                        {s.role === "agent" ? "Agente IA" : "Cliente"}
                      </span>
                      {s.text}
                    </div>
                  </div>
                ))}
              </div>
            ) : call.transcript ? (
              <pre className={`max-h-72 overflow-y-auto whitespace-pre-wrap rounded-xl border p-3 text-sm ${tc.border} ${tc.text}`}>
                {call.transcript}
              </pre>
            ) : (
              <p className={`text-sm ${tc.textSecondary}`}>Aún no hay transcripción.</p>
            )}
          </div>

          {call.costs?.langfuseTraceId && (
            <p className={`text-[11px] ${tc.textSecondary}`}>
              <i className="ri-line-chart-line mr-1" /> Langfuse trace: {call.costs.langfuseTraceId}
            </p>
          )}
        </div>
      }
    />
  );
}

function Info({
  label,
  value,
  tc,
}: {
  label: string;
  value: string | null | undefined;
  tc: ReturnType<typeof buildThemeClasses>;
}) {
  return (
    <div>
      <p className={`text-[11px] ${tc.textSecondary}`}>{label}</p>
      <p className={`text-sm ${tc.text}`}>{value || "—"}</p>
    </div>
  );
}

function Chip({
  active,
  label,
  count,
  tone,
  onClick,
}: {
  active: boolean;
  label: string;
  count: number;
  tone?: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-medium transition-colors ${
        active
          ? "border-primary-500 bg-primary-500 text-white"
          : "border-line bg-card text-content-muted hover:border-primary-500"
      }`}
    >
      <span>{label}</span>
      <span className={`rounded-full px-2 py-0.5 text-xs ${active ? "bg-white/20 text-white" : tone ?? "bg-gray-400/15 text-gray-500"}`}>
        {count}
      </span>
    </button>
  );
}

